import min from 'lodash/min';
import round from 'lodash/round';
import sum from 'lodash/sum';
import sumBy from 'lodash/sumBy';
import type { BaseSubjectProps } from '../StudyModelLayout';

export type GridPosition = { lineNumber: number; span: number };

export const toCssGridPosition = (position: GridPosition) => `${position.lineNumber} / span ${position.span}`;

export const horizontalLayoutGridRowPositionForSemester = {
    first: 1,
    second: 3,
    both: toCssGridPosition({ lineNumber: 1, span: 3 }),
};

const pointsSelector = (subject: BaseSubjectProps) => subject.points;

const calculateForSingleRow = (subjects: BaseSubjectProps[]) => {
    const allPoints = subjects.map(pointsSelector);
    const allPointsNumber = sum(allPoints);
    const smallestAmountOfPoints = min(allPoints);

    if (smallestAmountOfPoints === undefined) {
        throw new Error('Array was empty');
    }

    const columnsCount = getColumnsCount(allPoints, allPointsNumber, smallestAmountOfPoints);
    const columnWidth = allPointsNumber / columnsCount;

    const gridColumnMapping = calculateSubjectsPosition([], subjects, [], columnsCount, columnWidth);

    return {
        columnsCount,
        gridColumnMapping,
    };
};

const calculateForTwoRows = (subjects: BaseSubjectProps[]) => {
    const firstSemesterSubjects = subjects.filter(subject => subject.semester === 'first');
    const secondSemesterSubjects = subjects.filter(subject => subject.semester === 'second');
    const bothSemestersSubjects = subjects.filter(subject => subject.semester === 'both');

    const firstSemesterPoints = sumBy(firstSemesterSubjects, pointsSelector);
    const secondSemesterPoints = sumBy(secondSemesterSubjects, pointsSelector);
    const bothSemestersPoints = sumBy(bothSemestersSubjects, pointsSelector);

    const allPoints = subjects.map(pointsSelector);

    const smallestAmountOfPoints = min(allPoints);

    if (smallestAmountOfPoints === undefined) {
        throw new Error('Array was empty');
    }

    const longerSemesterExclusivePoints =
        firstSemesterPoints >= secondSemesterPoints ? firstSemesterPoints : secondSemesterPoints;

    const longerSemesterPoints = longerSemesterExclusivePoints + bothSemestersPoints;
    const columnsCount = getColumnsCount(allPoints, longerSemesterPoints, smallestAmountOfPoints);
    const columnWidth = longerSemesterPoints / columnsCount;

    const gridColumnMapping = calculateSubjectsPosition(
        bothSemestersSubjects,
        firstSemesterSubjects,
        secondSemesterSubjects,
        columnsCount,
        columnWidth
    );

    return {
        columnsCount,
        gridColumnMapping,
    };
};

export const resolveHorizontalGridLayout = (subjects: BaseSubjectProps[], useSingleRow = false) => {
    if (subjects.length === 0) {
        return {
            columnsCount: 1,
            gridColumnMapping: {},
        };
    }

    return useSingleRow ? calculateForSingleRow(subjects) : calculateForTwoRows(subjects);
};

const MAX_COLUMNS_COUNT = 48;

const getColumnsCount = (allPoints: number[], longestSemesterPoints: number, smallestAmountOfPoints: number) => {
    let potentialWidth = smallestAmountOfPoints;
    let numberOfColumns: number | undefined = undefined;
    let localMinimum = Infinity;
    let nextDivider = 2;

    while (true) {
        const numberOfColumnsCandidate = round(longestSemesterPoints / potentialWidth);

        if (numberOfColumnsCandidate > MAX_COLUMNS_COUNT) {
            return numberOfColumns ?? MAX_COLUMNS_COUNT;
        }

        const columnWidth = longestSemesterPoints / numberOfColumnsCandidate;
        const sumOfPointsThatWillNotPerfectlyFitIntoCalculatedNumberOfColumns = sum(
            allPoints.map(point => point % columnWidth)
        );

        if (sumOfPointsThatWillNotPerfectlyFitIntoCalculatedNumberOfColumns === 0) {
            return numberOfColumnsCandidate;
        }

        if (sumOfPointsThatWillNotPerfectlyFitIntoCalculatedNumberOfColumns < localMinimum) {
            localMinimum = sumOfPointsThatWillNotPerfectlyFitIntoCalculatedNumberOfColumns;
            numberOfColumns = numberOfColumnsCandidate;
        }

        potentialWidth = round(smallestAmountOfPoints / nextDivider, 2);
        nextDivider = nextDivider + 1;
    }
};

const INITIAL_GRID_POSITION = 2;

const calculateSubjectsPosition = (
    allYearSubjects: BaseSubjectProps[],
    firstSemesterSubjects: BaseSubjectProps[],
    secondSemesterSubjects: BaseSubjectProps[],
    columnsCount: number,
    columnWidth: number
) => {
    let allYearPosition = INITIAL_GRID_POSITION;

    const result: Record<string, GridPosition> = {};

    const performPlacementAndGetNewPosition = (subject: BaseSubjectProps, position: number) => {
        const span = round(subject.points / columnWidth);

        result[subject.code] = {
            lineNumber: position,
            span,
        };

        return position + span;
    };

    for (const subject of allYearSubjects) {
        allYearPosition = performPlacementAndGetNewPosition(subject, allYearPosition);
    }

    let firstSemesterPosition = allYearPosition;
    let secondSemesterPosition = allYearPosition;

    let firstSemesterLastItem: string | undefined = undefined;
    let secondSemesterLastItem: string | undefined = undefined;

    for (const subject of firstSemesterSubjects) {
        firstSemesterPosition = performPlacementAndGetNewPosition(subject, firstSemesterPosition);

        firstSemesterLastItem = subject.code;
    }

    for (const subject of secondSemesterSubjects) {
        secondSemesterPosition = performPlacementAndGetNewPosition(subject, secondSemesterPosition);

        secondSemesterLastItem = subject.code;
    }

    // Due to rounding in calculations of columns count and width in rare cases it might happen that subjects will
    // fill less or more columns than calculated. In order to mitigate this issue we are either shortening last item's
    // span or stretching it to the border if it's in the longer semester.
    const adjustLastItem = (lastPosition: number, lastItemId: string | undefined, stretch: boolean) => {
        if (!lastItemId) {
            return;
        }

        const diff = lastPosition - columnsCount - INITIAL_GRID_POSITION;

        if ((diff < 0 && stretch) || diff > 0) {
            const item = result[lastItemId];

            item.span = item.span - diff;
        }
    };

    adjustLastItem(firstSemesterPosition, firstSemesterLastItem, firstSemesterPosition >= secondSemesterPosition);
    adjustLastItem(secondSemesterPosition, secondSemesterLastItem, secondSemesterPosition >= firstSemesterPosition);

    return result;
};
